import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  BarChart3,
  Users,
  Clock,
  Activity,
  RefreshCw,
  TrendingUp,
  Monitor,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UserAnalyticsPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/analytics/user-usage?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short',
    });
  };

  // Find max for bar chart scaling
  const maxMinutes = data?.daily_breakdown?.reduce((max, d) => Math.max(max, d.total_minutes), 0) || 1;

  return (
    <div className="space-y-6" data-testid="user-analytics-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="user-analytics-title">User Analytics</h1>
          <p className="text-slate-500 mt-1">
            Session tracking and usage reports by user.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics} data-testid="refresh-analytics">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <p className="text-xs text-muted-foreground uppercase">Active Users</p>
                </div>
                <p className="text-3xl font-bold mt-2" data-testid="total-active-users">{data.summary.total_users}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  <p className="text-xs text-muted-foreground uppercase">Total Sessions</p>
                </div>
                <p className="text-3xl font-bold mt-2" data-testid="total-sessions">{data.summary.total_sessions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <p className="text-xs text-muted-foreground uppercase">Total Time</p>
                </div>
                <p className="text-3xl font-bold mt-2">
                  {Math.floor(data.summary.total_minutes / 60)}h {Math.round(data.summary.total_minutes % 60)}m
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-500" />
                  <p className="text-xs text-muted-foreground uppercase">Avg/User</p>
                </div>
                <p className="text-3xl font-bold mt-2">
                  {Math.round(data.summary.avg_minutes_per_user)}m
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Daily Usage Chart (CSS-only bar chart) */}
          {data.daily_breakdown?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Daily Usage (Minutes)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
                  {data.daily_breakdown.map((day, idx) => {
                    const height = Math.max(4, (day.total_minutes / maxMinutes) * 100);
                    return (
                      <div key={day.date || `day-${idx}`} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <span className="text-[10px] text-muted-foreground">{Math.round(day.total_minutes)}m</span>
                        <div
                          className="w-full bg-blue-500 rounded-t-sm transition-all duration-300 min-w-[8px]"
                          style={{ height: `${height}%` }}
                          title={`${day.date}: ${day.total_minutes.toFixed(1)} min, ${day.unique_users} users, ${day.sessions} sessions`}
                        />
                        <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                          {formatShortDate(day.date)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-User Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="w-5 h-5 text-slate-600" />
                User Breakdown ({period})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Time Spent</TableHead>
                    <TableHead className="text-right">Pages Visited</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.users?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No session data yet. Usage tracking begins when users interact with the app.
                      </TableCell>
                    </TableRow>
                  ) : data.users?.map((user, idx) => (
                    <TableRow key={user.user_id} data-testid={`user-row-${idx}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-xs font-semibold text-blue-700">
                              {user.user_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-sm">{user.user_name}</span>
                          {idx === 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Top</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{user.user_email}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{user.total_sessions}</TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-sm font-medium">{user.time_display}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{user.unique_pages}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(user.last_active)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
